import React, { useCallback, useEffect, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  MonitorPlay, CreditCard, Mail, Rocket, CheckCircle2, Circle,
  FlaskConical, Zap, Loader2, KeyRound, Info, MessageSquare, DollarSign, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const TEST_CARDS = [
  { num: '4242 4242 4242 4242', label: 'Successful payment' },
  { num: '4000 0000 0000 9995', label: 'Card declined' },
  { num: '4000 0025 0000 3155', label: 'Requires 3D-Secure' },
];

function SectionCard({ icon: Icon, title, desc, children, testId }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center">
            <Icon className="h-4 w-4" />
          </span>
          {title}
        </CardTitle>
        {desc && <CardDescription>{desc}</CardDescription>}
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function PricingCard({ s, busy, onSave }) {
  const eff = s.pricing_effective || {};
  const stored = s.pricing || {};
  const [base, setBase] = useState('');
  const [doc, setDoc] = useState('');
  const [adm, setAdm] = useState('');
  const [stf, setStf] = useState('');

  // Blank input = "leave as is". Only what the owner typed is sent, so a field
  // they never touched keeps inheriting instead of being pinned to a copy.
  const submit = () => {
    const pricing = {};
    if (base !== '') pricing.base_price_monthly = Number(base);
    const seats = {};
    if (doc !== '') seats.doctor = Number(doc);
    if (adm !== '') seats.admin = Number(adm);
    if (stf !== '') seats.staff = Number(stf);
    if (Object.keys(seats).length) pricing.overage_prices = seats;
    if (!Object.keys(pricing).length) { toast.error('Change a price first'); return; }
    onSave({ pricing }, 'pricing', 'Pricing updated — existing subscriptions are being re-synced')
      .then((ok) => { if (ok) { setBase(''); setDoc(''); setAdm(''); setStf(''); } });
  };

  const overridden = (k, nested) => (nested ? (stored[nested] || {})[k] !== undefined : stored[k] !== undefined);
  const Row = ({ label, value, onChange, current, isOverride }) => (
    <div className="space-y-1">
      <Label className="text-xs font-medium flex items-center gap-1.5">
        {label}
        {isOverride
          ? <Badge variant="secondary" className="text-[10px]">edited</Badge>
          : <span className="text-[10px] text-muted-foreground">default</span>}
      </Label>
      <div className="flex items-center gap-2">
        <span className="text-sm tabular-nums w-16 text-muted-foreground">${Number(current ?? 0).toFixed(2)}</span>
        <Input type="number" min="0" step="1" placeholder="new" value={value}
               onChange={(e) => onChange(e.target.value)} className="h-8 w-28" />
      </div>
    </div>
  );

  return (
    <SectionCard icon={DollarSign} title="Platform pricing" testId="owner-pricing-card"
      desc="Applies to every practice that has no negotiated deal of its own. Changing a price re-syncs existing Stripe subscriptions, prorated to the day.">
      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Base plan / month" value={base} onChange={setBase}
             current={eff.base_price_monthly} isOverride={overridden('base_price_monthly')} />
        <Row label="Extra doctor seat" value={doc} onChange={setDoc}
             current={(eff.overage_prices || {}).doctor} isOverride={overridden('doctor', 'overage_prices')} />
        <Row label="Extra admin seat" value={adm} onChange={setAdm}
             current={(eff.overage_prices || {}).admin} isOverride={overridden('admin', 'overage_prices')} />
        <Row label="Extra medical staff seat" value={stf} onChange={setStf}
             current={(eff.overage_prices || {}).staff} isOverride={overridden('staff', 'overage_prices')} />
      </div>
      <p className="text-[11px] text-muted-foreground mt-3">
        Per-document pricing is set per practice (patient fee and our share) on the practice's own page —
        it varies by billing mode, so it is deliberately not a platform-wide number.
      </p>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" onClick={submit} disabled={busy === 'pricing'} data-testid="owner-pricing-save">
          {busy === 'pricing' ? 'Saving…' : 'Save pricing'}
        </Button>
        {Object.keys(stored).length > 0 && (
          <Button size="sm" variant="ghost" disabled={busy === 'pricing'}
            onClick={() => onSave({ pricing_reset: true }, 'pricing', 'Pricing reset to defaults')}
            data-testid="owner-pricing-reset">Reset to defaults</Button>
        )}
      </div>
    </SectionCard>
  );
}

// ADR 0003 blocker 4. `plan_c_invoice.readiness` refuses a taxable Plan C
// invoice until a permit is recorded here. Texas is the only state with a tax
// table in pricing_config, so it is the only one offered; the server validates.
const PERMIT_STATE = 'texas';

function SalesTaxCard({ s, busy, onSave }) {
  const permits = s.sales_tax_permits || {};
  const current = permits[PERMIT_STATE] || {};
  const [number, setNumber] = useState('');
  const [from, setFrom] = useState('');

  const submit = () => {
    if (!number.trim()) { toast.error('Enter the permit number'); return; }
    onSave({ sales_tax_permit: { state: PERMIT_STATE, number: number.trim(), effective_from: from } },
      'permit', 'Sales-tax permit recorded')
      .then((ok) => { if (ok) { setNumber(''); setFrom(''); } });
  };

  return (
    <SectionCard icon={KeyRound} title="Sales-tax permit (Plan C)" testId="owner-sales-tax-card"
      desc="A Plan C invoice that charges sales tax will not issue until the permit is recorded here. Tax-exempt organisations are not affected. Each site's own rate is set on its practice page.">
      {Object.keys(permits).length === 0 ? (
        <p className="text-sm text-muted-foreground mb-3">No permit recorded — taxable Plan C invoices are blocked.</p>
      ) : (
        <div className="space-y-1 mb-3">
          {Object.entries(permits).map(([state, pm]) => (
            <p key={state} className="text-sm">
              <span className="capitalize font-medium">{state}</span>: <span className="font-mono">{pm.number}</span>
              <span className="text-xs text-muted-foreground">
                {pm.effective_from ? ` · effective ${pm.effective_from}` : ''}
                {pm.recorded_by ? ` · recorded by ${pm.recorded_by}` : ''}
              </span>
            </p>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs font-medium">Texas permit number</Label>
          <Input value={number} onChange={(e) => setNumber(e.target.value)} maxLength={40}
                 placeholder={current.number || 'e.g. 3-12345-6789-0'} className="h-8 w-56" data-testid="owner-permit-number" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Effective from</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40" data-testid="owner-permit-from" />
        </div>
        <Button size="sm" onClick={submit} disabled={busy === 'permit'} data-testid="owner-permit-save">
          {busy === 'permit' ? 'Saving…' : current.number ? 'Replace permit' : 'Record permit'}
        </Button>
        {current.number && (
          <Button size="sm" variant="ghost" disabled={busy === 'permit'}
            onClick={() => onSave({ sales_tax_permit: { state: PERMIT_STATE, number: '' } }, 'permit', 'Permit removed')}
            data-testid="owner-permit-remove">Remove</Button>
        )}
      </div>
    </SectionCard>
  );
}

// ADR 0008: the one-time fee a patient pays us on a patient-direct request. The
// server is the only place the amount comes from; this is where the owner sets
// it. Whether the rail is on is the deployment's PATIENT_DIRECT_ENABLED, shown
// here but deliberately not a switch (decision 7).
function PatientDirectCard({ s, busy, onSave }) {
  const [fee, setFee] = useState('');
  const submit = () => {
    if (fee === '') { toast.error('Enter a fee in dollars'); return; }
    onSave({ patient_direct_fee: Number(fee) }, 'pdfee', 'Patient-direct fee saved')
      .then((ok) => { if (ok) setFee(''); });
  };
  return (
    <SectionCard icon={Inbox} title="Patient-direct requests" testId="owner-patient-direct-card"
      desc="A patient whose doctor is not on DoctorPaperwork pays this one-time fee. The card is held at request and charged only when a practice accepts. A change applies to new requests only.">
      <p className="text-sm mb-3">
        Status:{' '}
        {s.patient_direct_enabled
          ? <Badge variant="secondary">On for this deployment</Badge>
          : <Badge variant="outline">Off (PATIENT_DIRECT_ENABLED)</Badge>}
        <span className="ml-2 text-muted-foreground">Current fee: <b>${Number(s.patient_direct_fee_effective || 0).toFixed(2)}</b>{s.patient_direct_fee == null ? ' (default)' : ''}</span>
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-1">
          <Label className="text-xs font-medium">New fee (USD)</Label>
          <Input type="number" min="0.5" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)}
                 className="h-8 w-32" data-testid="owner-pd-fee" />
        </div>
        <Button size="sm" onClick={submit} disabled={busy === 'pdfee'} data-testid="owner-pd-fee-save">
          {busy === 'pdfee' ? 'Saving…' : 'Save fee'}
        </Button>
        {s.patient_direct_fee != null && (
          <Button size="sm" variant="ghost" disabled={busy === 'pdfee'}
            onClick={() => onSave({ patient_direct_fee: null }, 'pdfee', 'Fee reset to the default')}>Reset to default</Button>
        )}
      </div>
    </SectionCard>
  );
}

function ChecklistRow({ done, label, hint }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {done
        ? <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        : <Circle className="h-4 w-4 text-muted-foreground/50 mt-0.5 shrink-0" />}
      <div>
        <span className={done ? '' : 'text-muted-foreground'}>{label}</span>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </li>
  );
}

export default function OwnerSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState('');
  const [testKey, setTestKey] = useState('');
  const [liveKey, setLiveKey] = useState('');
  const [testHook, setTestHook] = useState('');
  const [liveHook, setLiveHook] = useState('');
  const [demoInbox, setDemoInbox] = useState('');
  const [demoSms, setDemoSms] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/owner/platform-settings');
      setS(r.data);
      setDemoInbox(r.data.demo_email_redirect || '');
      setDemoSms(r.data.demo_sms_redirect || '');
    } catch (e) {
      toast.error(errText(e, 'Could not load platform settings'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload, busyKey, okMsg) => {
    setBusy(busyKey);
    try {
      const r = await api.patch('/owner/platform-settings', payload);
      setS(r.data);
      toast.success(okMsg);
      return true;
    } catch (e) {
      toast.error(errText(e, 'Update failed'));
      return false;
    } finally {
      setBusy('');
    }
  };

  if (!s) {
    return (
      <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-4" data-testid="owner-settings-loading">
        <Skeleton className="h-8 w-64" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40" />)}
      </div>
    );
  }

  const liveReady = s.stripe_live_key_configured;

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5" data-testid="owner-settings">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Platform Settings</h1>
        <p className="text-sm text-muted-foreground">Go-live switches — demo experience, payment mode and delivery status. Owner eyes only.</p>
      </div>

      {/* ---------------- Platform pricing ---------------- */}
      <PricingCard s={s} busy={busy} onSave={save} />
      <SalesTaxCard s={s} busy={busy} onSave={save} />
      <PatientDirectCard s={s} busy={busy} onSave={save} />

      {/* ---------------- Demo mode ---------------- */}
      <SectionCard
        icon={MonitorPlay}
        title="Demo Mode"
        desc="Controls the click-through demo across the whole platform."
        testId="owner-settings-demo-card"
      >
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <p className="text-sm font-medium flex items-center gap-2">
              Demo experience
              <Badge variant={s.demo_mode ? 'default' : 'secondary'} data-testid="demo-mode-badge">
                {s.demo_mode ? 'ON' : 'OFF'}
              </Badge>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              When ON: demo role logins, “Try as a Patient” and staff demo buttons on the landing/login pages, and mock payments are available.
              When OFF: customers only see the clean production experience — all demo affordances disappear and demo logins return 403.
            </p>
          </div>
          <Switch
            checked={s.demo_mode}
            disabled={busy === 'demo'}
            onCheckedChange={(v) => save({ demo_mode: v }, 'demo', v ? 'Demo mode enabled' : 'Demo mode disabled — customer-facing pages are now clean')}
            data-testid="demo-mode-switch"
            aria-label="Toggle demo mode"
          />
        </div>
        {!s.demo_mode && (
          <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Demo is off. You can still sign in with real accounts; flip this back ON anytime for sales walkthroughs.
          </p>
        )}
        <div className="mt-3 flex items-center justify-between rounded-lg border border-dashed p-3">
          <div>
            <p className="text-sm font-medium">Reset to demo defaults</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              One-click rollback: Demo Mode ON, Stripe back to test mode, demo email/SMS redirects restored.
              Your live keys are kept (just deactivated) — safe to use anytime.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={busy === 'reset'} data-testid="reset-demo-defaults-btn">
                {busy === 'reset' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="reset-demo-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset platform to demo defaults?</AlertDialogTitle>
                <AlertDialogDescription>
                  This turns Demo Mode ON, switches Stripe to TEST mode, and restores the demo email/SMS
                  redirects. No keys or data are deleted. Real charging stops until you switch back to Live.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-demo-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="reset-demo-confirm"
                  onClick={async () => {
                    setBusy('reset');
                    try {
                      const r = await api.post('/owner/platform-settings/reset-demo');
                      setS(r.data);
                      setDemoInbox(r.data.demo_email_redirect || '');
                      setDemoSms(r.data.demo_sms_redirect || '');
                      toast.success('Platform reset to demo defaults');
                    } catch (e) {
                      toast.error(errText(e, 'Could not reset to demo defaults'));
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  Reset to demo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </SectionCard>

      {/* ---------------- Stripe ---------------- */}
      <SectionCard
        icon={CreditCard}
        title="Payments — Stripe"
        desc="Switch between sandbox (test) and real (live) charging without redeploying."
        testId="owner-settings-stripe-card"
      >
        <div className="space-y-4">
          {/* Mode selector */}
          <div>
            <Label className="text-xs text-muted-foreground">Charging mode</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2 max-w-sm">
              <button
                type="button"
                data-testid="stripe-mode-test"
                onClick={() => s.stripe_mode !== 'test' && save({ stripe_mode: 'test' }, 'mode', 'Switched to Stripe TEST mode')}
                disabled={busy === 'mode'}
                className={cn(
                  'flex items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
                  s.stripe_mode === 'test'
                    ? 'border-primary bg-primary/5 font-medium ring-1 ring-primary'
                    : 'hover:bg-accent text-muted-foreground',
                )}
              >
                <FlaskConical className="h-4 w-4 text-primary" />
                <div className="text-left">
                  <div>Test (sandbox)</div>
                  <div className="text-[11px] font-normal text-muted-foreground">No real money moves</div>
                </div>
              </button>
              <button
                type="button"
                data-testid="stripe-mode-live"
                onClick={() => s.stripe_mode !== 'live' && save({ stripe_mode: 'live' }, 'mode', 'Switched to Stripe LIVE mode — real cards will be charged')}
                disabled={busy === 'mode' || !liveReady}
                className={cn(
                  'flex items-center gap-2 rounded-lg border p-3 text-sm transition-colors',
                  s.stripe_mode === 'live'
                    ? 'border-primary bg-primary/5 font-medium ring-1 ring-primary'
                    : liveReady ? 'hover:bg-accent text-muted-foreground' : 'opacity-50 cursor-not-allowed text-muted-foreground',
                )}
              >
                <Zap className="h-4 w-4 text-amber-600" />
                <div className="text-left">
                  <div>Live (real charges)</div>
                  <div className="text-[11px] font-normal text-muted-foreground">
                    {liveReady ? 'Charges real cards' : 'Add a live key to unlock'}
                  </div>
                </div>
              </button>
            </div>
          </div>

          <Separator />

          {/* Keys */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="stripe-test-key" className="flex items-center gap-1.5 text-xs">
                <KeyRound className="h-3.5 w-3.5" /> Test secret key
              </Label>
              <p className="text-[11px] text-muted-foreground" data-testid="stripe-test-key-current">
                {s.stripe_test_key_masked
                  ? <>Current: <span className="font-mono">{s.stripe_test_key_masked}</span>{s.stripe_test_key_source === 'env' ? ' (from server config)' : ''}</>
                  : 'Not configured'}
              </p>
              <div className="flex gap-2">
                <Input
                  id="stripe-test-key"
                  type="password"
                  placeholder="sk_test_..."
                  value={testKey}
                  onChange={(e) => setTestKey(e.target.value)}
                  data-testid="stripe-test-key-input"
                  autoComplete="off"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={!testKey.trim() || busy === 'testkey'}
                  onClick={async () => { if (await save({ stripe_test_key: testKey.trim() }, 'testkey', 'Test key saved')) setTestKey(''); }}
                  data-testid="stripe-test-key-save"
                >
                  {busy === 'testkey' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stripe-live-key" className="flex items-center gap-1.5 text-xs">
                <KeyRound className="h-3.5 w-3.5" /> Live secret key
              </Label>
              <p className="text-[11px] text-muted-foreground" data-testid="stripe-live-key-current">
                {s.stripe_live_key_masked
                  ? <>Current: <span className="font-mono">{s.stripe_live_key_masked}</span></>
                  : 'Not configured — required before switching to Live'}
              </p>
              <div className="flex gap-2">
                <Input
                  id="stripe-live-key"
                  type="password"
                  placeholder="sk_live_..."
                  value={liveKey}
                  onChange={(e) => setLiveKey(e.target.value)}
                  data-testid="stripe-live-key-input"
                  autoComplete="off"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={!liveKey.trim() || busy === 'livekey'}
                  onClick={async () => { if (await save({ stripe_live_key: liveKey.trim() }, 'livekey', 'Live key saved')) setLiveKey(''); }}
                  data-testid="stripe-live-key-save"
                >
                  {busy === 'livekey' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          </div>

          {/* Webhook signing secrets */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="stripe-test-hook" className="flex items-center gap-1.5 text-xs">
                <KeyRound className="h-3.5 w-3.5" /> Test webhook signing secret
              </Label>
              <p className="text-[11px] text-muted-foreground" data-testid="stripe-test-hook-current">
                {s.stripe_test_webhook_secret_masked
                  ? <>Current: <span className="font-mono">{s.stripe_test_webhook_secret_masked}</span>{s.stripe_test_webhook_secret_source === 'env' ? ' (from server config)' : ''}</>
                  : 'Not configured'}
              </p>
              <div className="flex gap-2">
                <Input
                  id="stripe-test-hook"
                  type="password"
                  placeholder="whsec_..."
                  value={testHook}
                  onChange={(e) => setTestHook(e.target.value)}
                  data-testid="stripe-test-hook-input"
                  autoComplete="off"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={!testHook.trim() || busy === 'testhook'}
                  onClick={async () => { if (await save({ stripe_test_webhook_secret: testHook.trim() }, 'testhook', 'Test webhook secret saved')) setTestHook(''); }}
                  data-testid="stripe-test-hook-save"
                >
                  {busy === 'testhook' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stripe-live-hook" className="flex items-center gap-1.5 text-xs">
                <KeyRound className="h-3.5 w-3.5" /> Live webhook signing secret
              </Label>
              <p className="text-[11px] text-muted-foreground" data-testid="stripe-live-hook-current">
                {s.stripe_live_webhook_secret_masked
                  ? <>Current: <span className="font-mono">{s.stripe_live_webhook_secret_masked}</span></>
                  : 'Not configured — required before going live'}
              </p>
              <div className="flex gap-2">
                <Input
                  id="stripe-live-hook"
                  type="password"
                  placeholder="whsec_..."
                  value={liveHook}
                  onChange={(e) => setLiveHook(e.target.value)}
                  data-testid="stripe-live-hook-input"
                  autoComplete="off"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="shrink-0"
                  disabled={!liveHook.trim() || busy === 'livehook'}
                  onClick={async () => { if (await save({ stripe_live_webhook_secret: liveHook.trim() }, 'livehook', 'Live webhook secret saved')) setLiveHook(''); }}
                  data-testid="stripe-live-hook-save"
                >
                  {busy === 'livehook' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          </div>

          {!s.stripe_webhook_ready && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="stripe-webhook-warning">
              No {s.stripe_mode} webhook signing secret is set, so Stripe webhooks are rejected. Payments still
              settle — the app asks Stripe directly when the patient returns from checkout — but subscription
              changes (card declines, cancellations) only refresh when someone opens the Billing page. Create the
              endpoint in Stripe → Developers → Webhooks, pointed at <span className="font-mono">/api/webhook/stripe</span>,
              then paste its signing secret above.
            </div>
          )}

          {/* Test cards helper */}
          {s.stripe_mode === 'test' && (
            <div className="rounded-lg bg-secondary p-3" data-testid="stripe-test-cards">
              <p className="text-xs font-medium mb-1.5">Demo checkout cards (test mode only)</p>
              <ul className="space-y-1">
                {TEST_CARDS.map((c) => (
                  <li key={c.num} className="flex items-center justify-between text-xs">
                    <span className="font-mono">{c.num}</span>
                    <span className="text-muted-foreground">{c.label}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[11px] text-muted-foreground mt-1.5">Any future expiry (e.g. 12/30), any CVC, any ZIP.</p>
            </div>
          )}
          {s.stripe_mode === 'live' && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="stripe-live-warning">
              Live mode is active — patient document fees will charge real cards through your Stripe account.
            </div>
          )}
        </div>
      </SectionCard>

      {/* ---------------- Email ---------------- */}
      <SectionCard
        icon={Mail}
        title="Email delivery — Amazon SES"
        desc="Patient links and document-ready notifications (link-only, no PHI in email bodies)."
        testId="owner-settings-email-card"
      >
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className={cn('h-2 w-2 rounded-full', s.email_enabled ? 'bg-primary' : 'bg-muted-foreground/40')} />
            <span data-testid="email-status">{s.email_enabled ? 'Connected — emails are being sent' : 'Not configured — deliveries are simulated'}</span>
          </div>
          <div className="text-muted-foreground">
            Sender: <span className="font-mono text-foreground" data-testid="email-sender">{s.sender_email}</span>
          </div>
        </div>
        {!s.email_enabled && (
          <p className="mt-2 text-xs text-muted-foreground flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            SES needs a region (AWS_REGION or SES_REGION), a verified sender identity in SENDER_EMAIL, and AWS
            credentials that allow ses:SendEmail — a Bedrock API key is not one. Until then deliveries are logged,
            not sent.
          </p>
        )}

        <Separator className="my-4" />

        <div className="space-y-1.5">
          <Label htmlFor="demo-inbox" className="flex items-center gap-1.5 text-xs">
            <MonitorPlay className="h-3.5 w-3.5" /> Demo traffic inbox
          </Label>
          <p className="text-[11px] text-muted-foreground">
            While Demo Mode is ON, every email the platform sends (patient links, document-ready notices, employer
            notifications, staff invites) is delivered to this inbox instead of the real recipient. The subject line is
            tagged with the original address so you can follow the full flow from one mailbox. Turn Demo Mode OFF to
            deliver to real recipients.
          </p>
          <div className="flex gap-2 max-w-md">
            <Input
              id="demo-inbox"
              type="email"
              placeholder="you@yourcompany.com"
              value={demoInbox}
              onChange={(e) => setDemoInbox(e.target.value)}
              data-testid="demo-inbox-input"
              autoComplete="off"
            />
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={busy === 'inbox' || demoInbox.trim().toLowerCase() === (s.demo_email_redirect || '')}
              onClick={() => save({ demo_email_redirect: demoInbox.trim() }, 'inbox', demoInbox.trim() ? 'Demo inbox saved — all demo emails route there now' : 'Demo redirect turned off')}
              data-testid="demo-inbox-save"
            >
              {busy === 'inbox' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
          {s.demo_email_redirect && s.demo_mode && (
            <p className="text-[11px] text-primary" data-testid="demo-inbox-active">
              Active: all outbound email currently routes to {s.demo_email_redirect}.
            </p>
          )}
        </div>

        <Separator className="my-4" />

        <div className="space-y-1.5">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Text messages (Twilio)</span>
            {s.sms_enabled
              ? <Badge variant="outline" className="text-[10px] text-primary border-primary/40" data-testid="sms-status-badge">Connected — from {s.sms_from_number}</Badge>
              : <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="sms-status-badge">Not configured</Badge>}
          </div>
          <Label htmlFor="demo-sms" className="text-xs">Demo traffic phone</Label>
          <p className="text-[11px] text-muted-foreground">
            While Demo Mode is ON, every text the platform sends (patient intake links, document-ready notices,
            payment receipts, staff work alerts) is delivered to this phone instead of the real recipient, prefixed
            with the original number. On a Twilio trial, this number must be verified in your Twilio console.
          </p>
          <div className="flex gap-2 max-w-md">
            <Input
              id="demo-sms"
              type="tel"
              placeholder="+1 555 123 4567"
              value={demoSms}
              onChange={(e) => setDemoSms(e.target.value)}
              data-testid="demo-sms-input"
              autoComplete="off"
            />
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={busy === 'sms' || demoSms.trim() === (s.demo_sms_redirect || '')}
              onClick={() => save({ demo_sms_redirect: demoSms.trim() }, 'sms', demoSms.trim() ? 'Demo phone saved — all demo texts route there now' : 'Demo SMS redirect turned off')}
              data-testid="demo-sms-save"
            >
              {busy === 'sms' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
          {s.demo_sms_redirect && s.demo_mode && (
            <p className="text-[11px] text-primary" data-testid="demo-sms-active">
              Active: all outbound texts currently route to {s.demo_sms_redirect}.
            </p>
          )}
          {s.sms_enabled && (
            <Button
              size="sm"
              variant="secondary"
              className="mt-1"
              disabled={busy === 'smstest'}
              data-testid="sms-test-button"
              onClick={async () => {
                setBusy('smstest');
                try {
                  const r = await api.post('/owner/sms-test', {});
                  if (r.data?.event === 'sms_sent' && r.data?.delivery_status === 'delivered') {
                    toast.success(`Test text DELIVERED to ${r.data.to} — check your phone`);
                  } else if (r.data?.event === 'sms_sent' && ['undelivered', 'failed'].includes(r.data?.delivery_status)) {
                    toast.warning('Twilio accepted the text but the carrier blocked it', {
                      description: r.data?.hint || `Carrier error ${r.data?.delivery_error_code || 'unknown'} — check Twilio Console logs.`,
                      duration: 12000,
                    });
                  } else if (r.data?.event === 'sms_sent') {
                    toast.info(`Text sent to ${r.data.to} (status: ${r.data?.delivery_status || 'queued'}) — give it a few seconds`);
                  } else {
                    toast.warning('Twilio did not accept the message yet', {
                      description: (r.data?.reason || '').includes('compliance profile')
                        ? 'Your Trust Hub KYC profile is still pending approval. Try again once Twilio approves it.'
                        : r.data?.reason || 'Unknown reason',
                      duration: 9000,
                    });
                  }
                } catch (e) {
                  toast.error(errText(e, 'Could not send the test text'));
                } finally {
                  setBusy(null);
                }
              }}
            >
              {busy === 'smstest' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send test text'}
            </Button>
          )}
        </div>
      </SectionCard>

      {/* ---------------- Go-live checklist ---------------- */}
      <SectionCard
        icon={Rocket}
        title="Go-live checklist"
        desc="Flip these before onboarding real practices with real patients."
        testId="owner-settings-checklist-card"
      >
        <ul className="space-y-2.5">
          <ChecklistRow done={!s.demo_mode} label="Demo Mode switched OFF" hint="Hides all demo buttons and disables demo logins." />
          <ChecklistRow done={s.stripe_live_key_configured} label="Stripe live key configured" />
          <ChecklistRow done={s.stripe_live_webhook_secret_configured} label="Stripe live webhook signing secret configured" hint="Stripe → Developers → Webhooks → your /api/webhook/stripe endpoint → Signing secret." />
          <ChecklistRow done={s.stripe_mode === 'live'} label="Stripe switched to Live mode" />
          <ChecklistRow done={!s.mock_pay_env} label="Mock payments disabled on the server" hint="Set ALLOW_MOCK_PAY=false in the backend environment." />
          <ChecklistRow done={s.email_enabled} label="Verified email domain" hint="SES domain identity verified (DKIM records) + production sender address in SENDER_EMAIL." />
          <ChecklistRow done={false} label="HIPAA-compliant hosting with signed BAAs" hint="Export to GitHub and deploy on AWS/GCP/Azure — see HIPAA_CHECKLIST.md." />
        </ul>
      </SectionCard>
    </div>
  );
}
